import { useEffect, useRef, type ReactNode } from 'react';
import { useAuth } from 'react-oidc-context';
import { AccessDenied } from '@/components/AccessDenied';
import { Spinner } from '@/components/primitives';
import { useCurrentUser } from '@/hooks/useCurrentUser';

function Centered({ children }: { children: ReactNode }) {
  return (
    <div style={{ minHeight: '100vh', display: 'grid', placeItems: 'center', background: 'var(--paper)' }}>
      <div className="row gap-10 muted">{children}</div>
    </div>
  );
}

// The OIDC states are checked in a fixed order: loading, then error, then unauthenticated.
// Reordering them causes a redirect loop on a failed silent renew.
export function AppBootstrap({ children }: { children: ReactNode }) {
  const auth = useAuth();
  const redirected = useRef(false);

  useEffect(() => {
    if (!auth.isLoading && !auth.isAuthenticated && !auth.error && !redirected.current) {
      redirected.current = true;
      void auth.signinRedirect();
    }
  }, [auth]);

  if (auth.isLoading) {
    return (
      <Centered>
        <Spinner /> Authenticating…
      </Centered>
    );
  }

  if (auth.error) {
    return (
      <AccessDenied
        title="Sign-in failed"
        message={auth.error.message}
        showLogout
      />
    );
  }

  if (!auth.isAuthenticated) {
    return (
      <Centered>
        <Spinner /> Redirecting to sign in…
      </Centered>
    );
  }

  return <AuthenticatedBootstrap>{children}</AuthenticatedBootstrap>;
}

function AuthenticatedBootstrap({ children }: { children: ReactNode }) {
  const { data: user, isLoading, isError, error } = useCurrentUser();

  if (isLoading) {
    return (
      <Centered>
        <Spinner /> Loading your account…
      </Centered>
    );
  }

  if (isError) {
    return (
      <AccessDenied
        title="Could not load your account"
        message={error instanceof Error ? error.message : 'The API did not respond.'}
        showLogout
      />
    );
  }

  if (!user?.clientRole) {
    return (
      <AccessDenied
        title="Authorization required"
        message="Your account has no assigned role. Contact an administrator to get access."
        showLogout
      />
    );
  }

  return <>{children}</>;
}
